import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import PawnObjectDesk from '../components/PawnObjectDesk';
import { useDebugData } from '../contexts/DebugDataContext';
import { hasUnnamedElectronicObjects } from '../domain/contracts/pawnObjectDesk';
import {
    SessionVerificationOverview,
    SessionVerificationRow,
    VerificationItem,
    VerificationItemKind,
    buildVerificationItemsFromDesk,
    resolveDeskExchangeRate,
    sumVerificationItemsRon,
    toDisplayText,
} from '../domain/sessionVerification';
import usePawnObjectDesk from '../hooks/usePawnObjectDesk';
import useRequest from '../hooks/useRequest';
import { parseContractReference } from '../domain/contractReference';

const numericValue = (value: unknown): number => Number(value ?? 0);

const createVerificationItemKey = (item: VerificationItem): string =>
    [
        item.source,
        item.kind,
        item.description,
        item.unitOfMeasure,
        item.quantity,
        item.unitPrice,
        item.valueRon,
        item.valueEur,
        item.purity,
        item.metalType,
        item.stoneWeightPercent,
        item.stoneWeightGrams,
        item.itemDate,
    ].join('-');

const SessionVerification = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const { runtimeData } = useDebugData();
    const [overview, setOverview] = useState<SessionVerificationOverview | null>(null);
    const [details, setDetails] = useState<VerificationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [tab, setTab] = useState(0);
    const [contractSearch, setContractSearch] = useState('');
    const [selected, setSelected] = useState<SessionVerificationRow | null>(null);
    const [deskOpen, setDeskOpen] = useState(false);
    const [unnamedConfirmOpen, setUnnamedConfirmOpen] = useState(false);
    const [notes, setNotes] = useState('');

    // The verification desk is the pawning desk itself (Delphi opens `AmanetareNew` with
    // `VerificareAmanet`), priced at the contract's own exchange rate.
    const desk = usePawnObjectDesk({
        cashPointId: runtimeData.cashPointId,
        exchangeRate: selected ? resolveDeskExchangeRate(selected) : 1,
        extendedDuration: false,
        prepareScratchTable: false,
    });
    const assessedItems = buildVerificationItemsFromDesk(desk.metalObjects, desk.electronicObjects);

    const loadOverview = useCallback(async () => {
        const cashPointId = runtimeData.cashPointId;
        if (!cashPointId) {
            setError(t('sessionVerification.noCashPoint'));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setOverview(
                await request<SessionVerificationOverview>({
                    path: `/cash-drawer/session-verification/${cashPointId}`,
                }),
            );
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : t('sessionVerification.loadError'));
        } finally {
            setLoading(false);
        }
    }, [request, runtimeData.cashPointId, t]);

    useEffect(() => {
        void loadOverview();
    }, [loadOverview]);

    const loadDetails = async (row: SessionVerificationRow): Promise<VerificationItem[]> => {
        const response = await request<{ items: Array<Record<string, unknown>> }>({
            path: `/cash-drawer/session-verification/contracts/${row.id}`,
        });
        return response.items.map(item => ({
            kind: item.kind as VerificationItemKind,
            source: item.source as 'current' | 'verified',
            description: toDisplayText(item.description),
            unitOfMeasure: toDisplayText(item.unitOfMeasure),
            quantity: numericValue(item.quantity),
            unitPrice: numericValue(item.unitPrice),
            valueRon: numericValue(item.valueRon),
            valueEur: numericValue(item.valueEur),
            purity: numericValue(item.purity),
            metalType: toDisplayText(item.metalType),
            stoneWeightPercent: numericValue(item.stoneWeightPercent),
            stoneWeightGrams: numericValue(item.stoneWeightGrams),
            itemDate: item.itemDate ? toDisplayText(item.itemDate) || undefined : undefined,
        }));
    };

    const showDetails = async (row: SessionVerificationRow) => {
        try {
            setDetails(await loadDetails(row));
        } catch (detailError) {
            setError(
                detailError instanceof Error
                    ? detailError.message
                    : t('sessionVerification.detailsError'),
            );
        }
    };

    const selectVerification = async (row: SessionVerificationRow) => {
        setSelected(row);
        setError(null);
        setSuccess(null);
        if (row.verified) {
            await showDetails(row);
            return;
        }
        // Blind re-assessment: the contract's original objects are not loaded, let alone
        // shown, until this verification has been registered.
        setDetails([]);
        setNotes('');
        desk.reset();
        setDeskOpen(true);
    };

    const handleContractSearch = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') return;
        // Accepts all three shapes an operator can produce: a legacy label, a label this
        // application printed, or a typed number. This flow verifies a whole contract, so
        // the object letter a canonical label also carries is not used here.
        const scanned = parseContractReference(contractSearch, { allowBareNumber: true });

        if (!scanned.ok) {
            setError(t('sessionVerification.contractNotFound'));
            return;
        }

        const contractNumber = String(scanned.value.contractNumber);
        const row = overview?.verifications.find(
            item =>
                item.contractNumber === contractNumber ||
                String(item.contractId) === contractNumber,
        );
        if (row) void selectVerification(row);
        else setError(t('sessionVerification.contractNotFound'));
    };

    const saveVerification = async () => {
        if (!selected) return;
        setUnnamedConfirmOpen(false);
        setSaving(true);
        setError(null);
        try {
            const response = await request<{ success: boolean; allVerified: boolean }>({
                path: `/cash-drawer/session-verification/contracts/${selected.id}`,
                method: 'POST',
                params: { notes, items: assessedItems },
            });
            setDeskOpen(false);
            setSuccess(
                response.allVerified
                    ? t('sessionVerification.sessionCompleted')
                    : t('sessionVerification.contractCompleted'),
            );
            await loadOverview();
            await showDetails({ ...selected, verified: 1 });
        } catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : t('sessionVerification.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const registerVerification = () => {
        // Same guard as the legacy desk: undescribed electronics/other rows need a confirmation
        if (hasUnnamedElectronicObjects(desk.electronicObjects)) {
            setUnnamedConfirmOpen(true);
            return;
        }
        void saveVerification();
    };

    const toggleChecklist = async (itemId: number, completed: boolean) => {
        if (!overview) return;
        const original = overview;
        setOverview({
            ...overview,
            checklist: overview.checklist.map(item =>
                item.id === itemId ? { ...item, completed } : item,
            ),
        });
        try {
            await request({
                path: `/cash-drawer/session-verification/checklist/${itemId}`,
                method: 'PUT',
                params: { completed },
            });
        } catch (checklistError) {
            setOverview(original);
            setError(
                checklistError instanceof Error
                    ? checklistError.message
                    : t('sessionVerification.checklistError'),
            );
        }
    };

    if (loading) return <CircularProgress />;

    let sessionLabel = t('sessionVerification.noPreviousSession');
    if (overview?.previousSession) {
        const { startTime, endTime } = overview.previousSession;
        const endTimeLabel = endTime
            ? new Date(endTime).toLocaleString()
            : t('sessionVerification.openSession');
        sessionLabel = `${new Date(startTime ?? '').toLocaleString()} - ${endTimeLabel}`;
    }

    return (
        <Box>
            <PageHeaderBanner
                icon={<FactCheckIcon />}
                title={t('sessionVerification.title')}
                subtitle={t('sessionVerification.subtitle')}
                accentColor="#059669"
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
                <Tab label={t('sessionVerification.objectsTab')} />
                <Tab label={t('sessionVerification.checklistTab')} />
            </Tabs>

            {tab === 0 && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {t('sessionVerification.shiftTitle', { session: sessionLabel })}
                    </Typography>
                    <TextField
                        size="small"
                        label={t('sessionVerification.contract')}
                        value={contractSearch}
                        onChange={event => setContractSearch(event.target.value)}
                        onKeyDown={handleContractSearch}
                        sx={{ mb: 2, width: 260 }}
                    />
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell>{t('sessionVerification.contract')}</TableCell>
                                <TableCell>{t('sessionVerification.object')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.differenceRon')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.differenceEur')}</TableCell>
                                <TableCell>{t('sessionVerification.verified')}</TableCell>
                                <TableCell>{t('sessionVerification.user')}</TableCell>
                                <TableCell>{t('sessionVerification.date')}</TableCell>
                                <TableCell>{t('sessionVerification.notes')}</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {overview?.verifications.map(row => (
                                    <TableRow
                                        hover
                                        key={row.id}
                                        onClick={() => void selectVerification(row)}
                                        selected={selected?.id === row.id}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor:
                                                Math.abs(row.differenceRon) > 30
                                                    ? 'error.light'
                                                    : undefined,
                                        }}
                                    >
                                        <TableCell>{row.contractNumber}</TableCell>
                                        <TableCell>{row.objectDescription}</TableCell>
                                        <TableCell align="right">{row.differenceRon.toFixed(2)}</TableCell>
                                        <TableCell align="right">{row.differenceEur.toFixed(2)}</TableCell>
                                        <TableCell><Checkbox checked={Boolean(row.verified)} disabled /></TableCell>
                                        <TableCell>{row.verifiedBy}</TableCell>
                                        <TableCell>{row.verifiedAt ? new Date(row.verifiedAt).toLocaleString() : ''}</TableCell>
                                        <TableCell>{row.notes}</TableCell>
                                    </TableRow>
                                ))}
                                {overview?.verifications.length === 0 && (
                                    <TableRow><TableCell colSpan={8} align="center">
                                        {t('sessionVerification.noContracts')}
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        {t('sessionVerification.differenceDetails')}
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell>{t('sessionVerification.source')}</TableCell>
                                <TableCell>{t('sessionVerification.type')}</TableCell>
                                <TableCell>{t('sessionVerification.description')}</TableCell>
                                <TableCell>{t('sessionVerification.unit')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.quantity')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.unitPrice')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.valueRon')}</TableCell>
                                <TableCell align="right">{t('sessionVerification.valueEur')}</TableCell>
                            </TableRow></TableHead>
                            <TableBody>{details.map(item => (
                                <TableRow key={createVerificationItemKey(item)}>
                                    <TableCell>{t(`sessionVerification.sources.${item.source}`)}</TableCell>
                                    <TableCell>{t(`sessionVerification.kinds.${item.kind}`)}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>{item.unitOfMeasure}</TableCell>
                                    <TableCell align="right">{item.quantity.toFixed(2)}</TableCell>
                                    <TableCell align="right">{item.unitPrice.toFixed(2)}</TableCell>
                                    <TableCell align="right">{item.valueRon.toFixed(2)}</TableCell>
                                    <TableCell align="right">{item.valueEur.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {tab === 1 && (
                <Paper sx={{ p: 3, maxWidth: 760 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                        <Typography variant="h6">{t('sessionVerification.checklistTitle')}</Typography>
                        <Typography variant="h6">
                            {t('sessionVerification.reportNumber', { number: overview?.reportNumber })}
                        </Typography>
                    </Box>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small" aria-label={t('sessionVerification.checklistTab')}>
                            <TableHead><TableRow>
                                <TableCell>{t('sessionVerification.checklistName')}</TableCell>
                                <TableCell align="center" sx={{ width: 160 }}>
                                    {t('sessionVerification.checklistCompleted')}
                                </TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {overview?.checklist.map(item => (
                                    <TableRow hover key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell align="center" padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={item.completed}
                                                onChange={event => void toggleChecklist(item.id, event.target.checked)}
                                                slotProps={{ input: { 'aria-label': item.name } }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {overview?.checklist.length === 0 && (
                                    <TableRow><TableCell colSpan={2} align="center">
                                        {t('sessionVerification.noChecklist')}
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog
                open={deskOpen}
                onClose={() => !saving && setDeskOpen(false)}
                fullScreen
                aria-labelledby="verification-desk-title"
            >
                <DialogTitle id="verification-desk-title">
                    {t('sessionVerification.verifyContract', { contract: selected?.contractNumber })}
                </DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('sessionVerification.blindAssessmentHint')}
                    </Alert>
                    {desk.isLoaded ? <PawnObjectDesk desk={desk} lockPrices /> : <CircularProgress />}
                    <Typography variant="subtitle1" sx={{ mt: 3 }}>
                        {t('sessionVerification.assessedTotal', {
                            amount: sumVerificationItemsRon(assessedItems).toFixed(2),
                        })}
                    </Typography>
                    {assessedItems.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('sessionVerification.noAssessedObjects')}
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={t('sessionVerification.notes')}
                        value={notes}
                        onChange={event => setNotes(event.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeskOpen(false)} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        data-testid="register-verification-button"
                        variant="contained"
                        onClick={registerVerification}
                        disabled={saving || !desk.isLoaded || assessedItems.length === 0}
                    >
                        {saving ? t('sessionVerification.saving') : t('sessionVerification.registerVerification')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={unnamedConfirmOpen} onClose={() => setUnnamedConfirmOpen(false)}>
                <DialogTitle>{t('sessionVerification.unnamedObjectsTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('sessionVerification.unnamedObjectsConfirm')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnnamedConfirmOpen(false)}>{t('common.no')}</Button>
                    <Button variant="contained" onClick={() => void saveVerification()}>
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SessionVerification;
